"use client";

import { memo, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { CommentItem, type ThreadComment } from "./CommentItem";
import { enterThread } from "@/lib/hooks/useThreadFocus";

const KOMMENTAR_HASH_RE = /^#kommentar-(\d+)$/;

// Subscribes to the URL hash via useSyncExternalStore rather than
// state-in-an-effect: it updates on `hashchange` (e.g. clicking a second
// notification link while already on this page), it's the React-recommended
// way to read external/browser state, and it sidesteps any SSR/hydration
// mismatch — the server snapshot is always "no target".
function subscribeToHashChange(onChange: () => void) {
  window.addEventListener("hashchange", onChange);
  return () => window.removeEventListener("hashchange", onChange);
}
function getHashSnapshot() {
  return window.location.hash;
}
function getServerHashSnapshot() {
  return "";
}

/** A top-level comment and everything nested under it, in server order. */
interface Branch {
  comment: ThreadComment;
  replies: ThreadComment[];
}

/**
 * `parentId` PLACES EVERY ROW
 *
 * Both shapes this component receives guarantee a parent appears before its
 * reply: `GET /posts/{id}/comments` answers depth-first with `parentId`
 * nulled at the top of what was asked for, and the feed card's bounded
 * preview (`PostComment`) is ancestor-closed and chronological. So one pass
 * over one id-keyed map groups everything: a row whose `parentId` is already
 * placed joins that branch; a row with no placeable parent opens its own.
 * `depth` is not load-bearing here — reading its absence as "every row is a
 * root" is exactly how OSO-439 rendered feed-card replies as top-level
 * comments (the wire had `parentId` the whole time).
 *
 * The depth fallback below covers one narrow edge: `threadPage`'s hydrate can
 * drop a middle row under RLS after the walk found it, leaving a deeper row
 * whose parent is not in the list. Depth-first order says it belongs to the
 * branch that is open at that point.
 */
function toBranches(comments: ThreadComment[]): Branch[] {
  const depthOf = (c: ThreadComment) => ("depth" in c ? c.depth : 0);
  const rootDepth = comments.length > 0 ? Math.min(...comments.map(depthOf)) : 0;
  const branches: Branch[] = [];
  const branchOf = new Map<number, Branch>();
  for (const comment of comments) {
    const owner =
      (comment.parentId != null ? branchOf.get(comment.parentId) : undefined) ??
      (depthOf(comment) > rootDepth ? branches[branches.length - 1] : undefined);
    const branch = owner ?? { comment, replies: [] };
    if (owner) owner.replies.push(comment);
    else branches.push(branch);
    branchOf.set(comment.id, branch);
  }
  return branches;
}

function engagementScore(c: ThreadComment): number {
  return c.reactions.counts.reduce((a, r) => a + r.count, 0);
}

// Stable comparator: higher score first, ties broken by older comment first.
// Ids are a sequence, so they are the chronological tiebreak the old
// `post_number` fallback was.
function byScoreDesc(a: Branch, b: Branch): number {
  const diff = engagementScore(b.comment) - engagementScore(a.comment);
  if (diff !== 0) return diff;
  const timeDiff =
    new Date(a.comment.createdAt).getTime() - new Date(b.comment.createdAt).getTime();
  if (timeDiff !== 0) return timeDiff;
  return a.comment.id - b.comment.id;
}

const FULL_THREAD_LIMIT = 10;

/**
 * How many rows a QUIET feed card shows (OSO-622) — the newest ones, plus the
 * ancestors that make them readable. The design says "latest 2–3"; three is a
 * taste call, and this constant is where to change it. Everything hidden is
 * one click away behind "Vis alle kommentarer" — and the rows are already in
 * the cache, so revealing them fetches nothing.
 */
const QUIET_PREVIEW_ROWS = 3;

/**
 * The newest `QUIET_PREVIEW_ROWS` rows plus their ancestor closure.
 *
 * Walked from the END because the preview is chronological, and closed over
 * `parentId` chains because a reply floating without its parent reads as an
 * answer to nothing — the same reason the server's preview selection is
 * ancestor-closed (`commentPreviewsFor`). Ancestors sit earlier in the array,
 * so the closure is one upward pass over an id-keyed map.
 */
function quietPreviewIds(comments: ThreadComment[]): Set<number> {
  // Keyed as plain `number`: the branded `EntityId<"post">` narrows to it,
  // and the closure walks `parentId`s that are numbers on both wire shapes.
  const byId = new Map<number, ThreadComment>(comments.map((c) => [c.id, c]));
  const ids = new Set<number>();
  for (let i = comments.length - 1; i >= 0 && ids.size < QUIET_PREVIEW_ROWS; i--) {
    ids.add(comments[i].id);
  }
  for (const id of [...ids]) {
    let current = byId.get(id);
    while (current?.parentId != null) {
      current = byId.get(current.parentId);
      if (!current || ids.has(current.id)) break;
      ids.add(current.id);
    }
  }
  return ids;
}

// Greedily pick top comments (+ their single top reply) until we reach the
// display limit. Returns the selected branches ordered by score and the chosen
// top reply for each (used as a pinned, always-visible reply).
function selectTopBranches(branches: Branch[]): { branch: Branch; topReply: ThreadComment | null }[] {
  const ranked = [...branches].sort(byScoreDesc);
  const selected: { branch: Branch; topReply: ThreadComment | null }[] = [];
  let count = 0;
  for (const branch of ranked) {
    if (count >= FULL_THREAD_LIMIT) break;
    const topReply =
      branch.replies.length > 0
        ? [...branch.replies].sort((a, b) =>
            byScoreDesc({ comment: a, replies: [] }, { comment: b, replies: [] })
          )[0]
        : null;
    selected.push({ branch, topReply });
    count += topReply ? 2 : 1;
  }
  return selected;
}

export const CommentThread = memo(function CommentThread({
  comments,
  postId,
  groupId,
  groupSlug,
  currentUserId,
  fullThread = false,
  feedPreview = false,
  unreadIds,
  replyCount,
  unreadCount,
}: {
  comments: ThreadComment[];
  postId: number;
  groupId?: number;
  /** The group's slug, for the @alle capability lookup (OSO-681). */
  groupSlug?: string;
  currentUserId?: number | null;
  fullThread?: boolean;
  /**
   * A FEED CARD'S PREVIEW, NOT A THREAD PAGE (OSO-622). Two render modes
   * branch on it before the modeA/modeC computation below:
   *
   *  - Mode U (something is new-to-you): the branches that CONTAIN an unread
   *    row render expanded, the fully-read ones fold behind "Vis N tidligere
   *    kommentarer".
   *  - Mode Q (nothing new): the newest `QUIET_PREVIEW_ROWS` rows plus their
   *    ancestors, behind "Vis alle kommentarer".
   *
   * Mode Q REPLACES modeA's everything-expanded rendering for feed cards — a
   * 9-comment card used to show all 9 — and it applies on EVERY feed,
   * including group feeds, per OSO-622 §4: `PostCard` derives it from
   * `variant`, while the HIGHLIGHTS stay home-feed-only via `unreadIds`.
   * The detail page keeps modeA/modeC untouched.
   */
  feedPreview?: boolean;
  /** The frozen per-mount unread set (`useUnreadSnapshot`) — home feed only. */
  unreadIds?: ReadonlySet<number>;
  /**
   * The card's honest whole-thread counts, passed down from `PostCard`
   * because the seed here is BOUNDED: the fold labels count what the thread
   * actually holds, and the local rows alone would undercount it.
   */
  replyCount?: number;
  unreadCount?: number;
}) {
  const [showAll, setShowAll] = useState(false);
  // Tracks "#kommentar-N" across client-side hash changes too — e.g. clicking
  // a second notification link ("#kommentar-5" -> "#kommentar-9") while
  // already on this page doesn't trigger a full navigation/remount, only a
  // `hashchange` event.
  const hash = useSyncExternalStore(subscribeToHashChange, getHashSnapshot, getServerHashSnapshot);
  const hashMatch = KOMMENTAR_HASH_RE.exec(hash);
  // THE ANCHOR IS THE COMMENT ID now, not its position in a topic: a post
  // number was only ever a per-topic index and v1 has none.
  const targetCommentId = hashMatch ? Number(hashMatch[1]) : null;
  // Remembers which target we've already scrolled to/highlighted, so a live-poll
  // refresh of `comments` (new array reference, same content) doesn't re-trigger it.
  const handledTargetRef = useRef<number | null>(null);

  // Scroll to and highlight the targeted comment once it's in the DOM. The
  // element may not exist on the first render — the comment tree can still be
  // loading, or the target may be a collapsed reply / truncated long-thread
  // comment that only mounts once we force it open (below) — so retry briefly
  // instead of giving up after a single lookup.
  useEffect(() => {
    if (targetCommentId == null) return;
    if (handledTargetRef.current === targetCommentId) return;

    const elementId = `kommentar-${targetCommentId}`;
    let cancelled = false;
    let attempts = 0;
    let highlightTimer: ReturnType<typeof setTimeout> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const attempt = () => {
      if (cancelled) return;
      const el = document.getElementById(elementId);
      if (el) {
        handledTargetRef.current = targetCommentId;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // Restart the flash animation even if this element was highlighted before.
        el.classList.remove("comment-highlight");
        void el.offsetWidth;
        el.classList.add("comment-highlight");
        highlightTimer = setTimeout(() => el.classList.remove("comment-highlight"), 2000);
        return;
      }
      attempts += 1;
      if (attempts < 30) {
        retryTimer = setTimeout(attempt, 150);
      }
    };

    attempt();

    return () => {
      cancelled = true;
      clearTimeout(highlightTimer);
      clearTimeout(retryTimer);
    };
  }, [targetCommentId, comments.length]);

  if (comments.length === 0) return null;

  const branches = toBranches(comments);

  // If the hash target exists in this thread, figure out which branch owns it
  // so we can force that branch open and skip the long-thread truncation below
  // — otherwise a deep link into a reply, or into a comment that didn't make
  // the "top comments" cut, would silently land on nothing.
  const hasTarget =
    targetCommentId != null && comments.some((c) => c.id === targetCommentId);
  const targetBranchId =
    hasTarget && targetCommentId != null
      ? branches.find(
          (b) =>
            b.comment.id === targetCommentId ||
            b.replies.some((r) => r.id === targetCommentId)
        )?.comment.id ?? null
      : null;

  // Mode A: short threads render every comment with replies auto-expanded.
  // Mode C: long threads with many top-level comments greedily show top ones.
  // A pending deep-link target always bypasses Mode C's truncation.
  const modeA = fullThread && comments.length < FULL_THREAD_LIMIT;
  const modeC = fullThread && !showAll && !hasTarget && branches.length >= FULL_THREAD_LIMIT;

  const wrapperClass = "mx-gutter border-t border-border py-3 space-y-3";

  // Both feed modes stand down for a deep link (`hasTarget`), exactly as the
  // long-thread truncation does: the reader was sent to a specific comment,
  // and a fold between them and it would land the link on a button. `showAll`
  // stands them down too — asking for the past entered the thread, and from
  // then on the card renders everything with `unreadIds` still applied, so
  // revealing history does not un-highlight what was new.
  if (feedPreview && !showAll && !hasTarget && (unreadIds?.size ?? 0) > 0) {
    // Mode U: the branches that CONTAIN something unread render whole and
    // expanded — the read parent of an unread reply renders automatically and
    // unhighlighted, because the preview is ancestor-closed (server side in
    // `commentPreviewsFor`, and pinned again by this component's header) — and
    // the fully-read branches fold.
    const unreadBranches = branches.filter(
      (b) => unreadIds!.has(b.comment.id) || b.replies.some((r) => unreadIds!.has(r.id)),
    );
    const readBranches = branches.filter((b) => !unreadBranches.includes(b));
    const hiddenLocalRows = readBranches.reduce(
      (sum, b) => sum + 1 + b.replies.length,
      0,
    );
    /**
     * "Tidligere" counts the WHOLE thread's read comments, not the bounded
     * seed's: the wire's `replyCount` minus the sticky `unreadCount`, with
     * the locally hidden rows as the floor — the counts are two honest views
     * of the same thread and the larger is the honest label. On a >1000-row
     * thread (`MAX_THREAD_PAGES`) it can disagree by a hair with what
     * expansion reveals; tolerated, same as `useComments`' own ceiling.
     */
    const priorCount = Math.max(
      (replyCount ?? 0) - (unreadCount ?? 0),
      hiddenLocalRows,
    );
    return (
      <div className={wrapperClass}>
        {unreadBranches.map((branch) => (
          <CommentItem
            key={branch.comment.id}
            comment={branch.comment}
            postId={postId}
            groupId={groupId}
            groupSlug={groupSlug}
            currentUserId={currentUserId}
            replies={branch.replies}
            defaultExpanded
            unreadIds={unreadIds}
          />
        ))}
        {priorCount > 0 && (
          <button
            onClick={() => {
              // Asking for the past is asking for the thread: the bounded
              // seed cannot answer alone, so entering it starts the live read
              // that brings what the preview never carried.
              enterThread(postId);
              setShowAll(true);
            }}
            className="text-xs font-semibold text-primary hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors py-1"
          >
            Vis {priorCount} tidligere kommentar{priorCount === 1 ? "" : "er"}
          </button>
        )}
      </div>
    );
  }

  if (feedPreview && !showAll && !hasTarget) {
    // Mode Q: nothing is new, so the card shrinks to the newest rows plus the
    // ancestors that make them readable — the quiet default OSO-622 trades
    // against the old everything-expanded feed card.
    const shownIds = quietPreviewIds(comments);
    const shownBranches = branches
      .filter((b) => shownIds.has(b.comment.id))
      .map((b) => ({
        comment: b.comment,
        replies: b.replies.filter((r) => shownIds.has(r.id)),
      }));
    // Hidden rows exist either locally (the seed holds more than the quiet
    // cut) or beyond the seed (the thread holds more than the bounded
    // preview) — both mean the same button.
    const hasHidden =
      comments.length > shownIds.size || (replyCount ?? 0) > shownIds.size;
    return (
      <div className={wrapperClass}>
        {shownBranches.map((branch) => (
          <CommentItem
            key={branch.comment.id}
            comment={branch.comment}
            postId={postId}
            groupId={groupId}
            groupSlug={groupSlug}
            currentUserId={currentUserId}
            replies={branch.replies}
            defaultExpanded
          />
        ))}
        {hasHidden && (
          <button
            onClick={() => {
              enterThread(postId);
              setShowAll(true);
            }}
            className="text-xs font-semibold text-primary hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors py-1"
          >
            Vis alle kommentarer
          </button>
        )}
      </div>
    );
  }

  if (modeC) {
    return (
      <div className={wrapperClass}>
        {selectTopBranches(branches).map(({ branch, topReply }) => (
          <CommentItem
            key={branch.comment.id}
            comment={branch.comment}
            postId={postId}
            groupId={groupId}
            groupSlug={groupSlug}
            currentUserId={currentUserId}
            replies={branch.replies}
            pinnedReplies={topReply ? [topReply] : undefined}
          />
        ))}
        <button
          onClick={() => {
            // Asking for all of them is asking for the thread: the card is
            // seeded with a BOUNDED preview, so the rest arrives with the live
            // read this opens.
            enterThread(postId);
            setShowAll(true);
          }}
          className="text-xs font-semibold text-primary hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors py-1"
        >
          Vis alle kommentarer
        </button>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {branches.map((branch) => (
        <CommentItem
          key={branch.comment.id}
          comment={branch.comment}
          postId={postId}
          groupId={groupId}
          groupSlug={groupSlug}
          currentUserId={currentUserId}
          replies={branch.replies}
          defaultExpanded={modeA}
          forceExpandReplies={branch.comment.id === targetBranchId}
          unreadIds={unreadIds}
        />
      ))}
    </div>
  );
});
